import { Breadcrumb, Button, Card, Drawer, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import SchedulerJobService from "../../services/SchedulerJobService";
import { ISchedulerJob, ISchedulerJobRun } from "../../types/MongerlabCustomer";
import { convertDate, openNotification } from "../../utils";
import SettingsRouter from "./SettingsRouter";

const { Title } = Typography;

export const SchedulerJobs = () => {
  const [jobs, setJobs] = useState<ISchedulerJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [runsDrawerOpen, setRunsDrawerOpen] = useState(false);
  const [selectedJobKey, setSelectedJobKey] = useState<string | null>(null);
  const [runs, setRuns] = useState<ISchedulerJobRun[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [triggerLoading, setTriggerLoading] = useState<string | null>(null);

  const loadJobs = () => {
    setLoading(true);
    SchedulerJobService.getAllJobs()
      .then((r) => setJobs(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadJobs();
  }, []);

  const openRuns = (jobKey: string) => {
    setSelectedJobKey(jobKey);
    setRunsDrawerOpen(true);
    setRunsLoading(true);
    SchedulerJobService.getJobRuns(jobKey)
      .then((r) => setRuns(r.data.data))
      .finally(() => setRunsLoading(false));
  };

  const triggerJob = (jobKey: string) => {
    setTriggerLoading(jobKey);
    SchedulerJobService.triggerJob(jobKey)
      .then(() => {
        openNotification("success", "Success", "Job triggered successfully");
        loadJobs();
      })
      .catch(console.error)
      .finally(() => setTriggerLoading(null));
  };

  const statusColor = (status?: string) => {
    if (status === "SUCCESS") return "green";
    if (status === "FAILED") return "red";
    if (status === "RUNNING") return "blue";
    return "default";
  };

  const columns: ColumnsType<ISchedulerJob> = [
    { title: "Job Key", dataIndex: "jobKey", key: "jobKey", width: 220 },
    { title: "Description", dataIndex: "description", key: "description" },
    {
      title: "Enabled",
      dataIndex: "enabled",
      key: "enabled",
      width: 90,
      render: (v) => (v ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>),
    },
    { title: "Schedule", key: "schedule", width: 160, render: (_, row) => row.cron || (row.fixedRateMs ? `${row.fixedRateMs}ms` : "-") },
    {
      title: "Last Run",
      key: "lastRun",
      width: 180,
      render: (_, row) => (row.lastRunStartedAt ? convertDate(row.lastRunStartedAt) : "-"),
    },
    {
      title: "Last Status",
      dataIndex: "lastRunStatus",
      key: "lastRunStatus",
      width: 110,
      render: (v) => (v ? <Tag color={statusColor(v)}>{v}</Tag> : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 200,
      render: (_, row) => (
        <>
          <Button size="small" style={{ marginRight: 8 }} onClick={() => openRuns(row.jobKey)}>
            History
          </Button>
          {row.triggerable && (
            <Button
              size="small"
              type="primary"
              loading={triggerLoading === row.jobKey}
              onClick={() => triggerJob(row.jobKey)}
            >
              Run Now
            </Button>
          )}
        </>
      ),
    },
  ];

  const runColumns: ColumnsType<ISchedulerJobRun> = [
    { title: "Status", dataIndex: "status", key: "status", render: (v) => <Tag color={statusColor(v)}>{v}</Tag> },
    { title: "Trigger", dataIndex: "triggerType", key: "triggerType" },
    { title: "Started", dataIndex: "startedAt", key: "startedAt", render: (v) => convertDate(v) },
    { title: "Duration (ms)", dataIndex: "durationMs", key: "durationMs" },
    { title: "Summary", dataIndex: "summary", key: "summary", ellipsis: true },
    { title: "Error", dataIndex: "errorMessage", key: "errorMessage", ellipsis: true },
  ];

  return (
    <>
      <Breadcrumb items={[{ title: <NavLink to="/settings">Settings</NavLink> }, { title: "Jobs" }]} />
      <SettingsRouter />
      <Title level={4} style={{ margin: "16px 0" }}>
        Scheduler Jobs
      </Title>
      <Card>
        <Table rowKey="jobKey" loading={loading} columns={columns} dataSource={jobs} pagination={false} scroll={{ x: 1100 }} />
      </Card>

      <Drawer
        title={`Run History: ${selectedJobKey}`}
        open={runsDrawerOpen}
        onClose={() => setRunsDrawerOpen(false)}
        width={900}
      >
        <Table rowKey="id" loading={runsLoading} columns={runColumns} dataSource={runs} pagination={false} />
      </Drawer>
    </>
  );
};

export default SchedulerJobs;
